import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useOnboarding } from '../../../context/OnboardingContext'

interface TimeInfo {
    remainingTime: number | null
    teamsReadyTime: number | null
    isTimeUp: boolean
}

export const useRemainingTime = (): TimeInfo => {
    const { config } = useOnboarding()
    const [timeInfo, setTimeInfo] = useState<TimeInfo>({
        remainingTime: null,
        teamsReadyTime: null,
        isTimeUp: false,
    })

    // Add ref for tracking start time
    const timerStartRef = useRef(Date.now())

    // Memoize the calculation of mandatory videos length
    const mandatoryVideosLength = useMemo(() => {
        if (!config) return 0
        return config.dailyContent.onboarding.videos
            .filter(
                video =>
                    (video.metadata.phase === 'pre' || video.metadata.phase === 'main') &&
                    video.metadata.category !== 'icebreaker'
            )
            .reduce((sum, video) => sum + video.length, 0)
    }, [config])

    // Memoize the teams ready time calculation
    const teamsReadyTimeMs = useMemo(() => {
        if (!config) return null
        const {
            courseStartTime,
            timeSettings: { extraTime },
        } = config.onboardingConfig
        const startTimeMs = courseStartTime * 1000
        return startTimeMs + (mandatoryVideosLength + extraTime) * 1000
    }, [config, mandatoryVideosLength])

    // Memoize the calculation function
    const calculateRemainingTime = useCallback(() => {
        if (!config || !teamsReadyTimeMs) return null

        const currentTimeMs = Date.now()
        const remainingSeconds = Math.floor((teamsReadyTimeMs - currentTimeMs) / 1000)

        // Calculate timer drift
        const expectedTime = timerStartRef.current + 1000 * Math.floor((currentTimeMs - timerStartRef.current) / 1000)
        const drift = currentTimeMs - expectedTime

        // Log timing information
        if (remainingSeconds % 30 === 0) {
            console.log('Time status:', {
                // Your existing logs...
                courseStartTime: new Date(config.onboardingConfig.courseStartTime * 1000).toLocaleString(),
                teamsReadyTime: new Date(teamsReadyTimeMs).toLocaleString(),
                currentTime: new Date(currentTimeMs).toLocaleString(),
                // Add drift tracking logs
                expectedTimerTime: new Date(expectedTime).toLocaleString(),
                actualTimerTime: new Date(currentTimeMs).toLocaleString(),
                driftMs: drift,
                timerRunningFor: `${Math.floor((currentTimeMs - timerStartRef.current) / 1000)}s`,
            })
        }

        return {
            remainingTime: remainingSeconds > 0 ? remainingSeconds : 0,
            teamsReadyTime: teamsReadyTimeMs,
            isTimeUp: remainingSeconds <= 0,
        }
    }, [config, teamsReadyTimeMs])

    useEffect(() => {
        // Reset timer start reference on effect
        timerStartRef.current = Date.now()

        // Initial calculation
        setTimeInfo(calculateRemainingTime() || timeInfo)

        const timer = setInterval(() => {
            setTimeInfo(calculateRemainingTime() || timeInfo)
        }, 1000)

        // Add visibility change handler
        const handleVisibility = () => {
            if (!document.hidden) {
                // Reset timer start when becoming visible
                timerStartRef.current = Date.now()
                setTimeInfo(calculateRemainingTime() || timeInfo)
            }
        }
        document.addEventListener('visibilitychange', handleVisibility)

        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [calculateRemainingTime])

    return timeInfo
}
